import React from 'react'
import _ from 'lodash'
import { shortName } from './buildCategoryTree'
import { XIcon } from './icons'

// Mirrors FiltersApplied data extraction but renders with the new design
// (outlined chips on a single horizontal row, with leading "Clear all").
// Preserves locked-chip semantics: locked entries render without an X.
//
// Keeps Theme/Person/Location/Activity/Donation Type chips visible even
// though they have no dropdown triggers — chip removal still works.

// DEV-2980: the storefront's chip (.fb-chip): the value only, with an x
// unless it is locked by the page (a category or theme landing page).
function Chip({ group, label, locked, onRemove }) {
  return (
    <span className="fb-chip" title={group}>
      {label}
      {!locked && (
        <button type="button" className="fb-chip-x" onClick={onRemove} aria-label={`Remove ${group} ${label}`}>
          <XIcon />
        </button>
      )}
    </span>
  )
}

export default function ChipsRow({ model, changeState, search, onClearAll, showClearAll }) {
  const filters = model.filters

  function removeAndRefetch(mutator) {
    const updated = _.cloneDeep(model)
    mutator(updated.filters)
    changeState(updated)
    search({ m: updated })
    document.dispatchEvent(new CustomEvent('update-listing-filters', {
      detail: { filters: updated.filters }
    }))
  }

  const chips = []

  // Categories — selected keys are the full "Parent >> Child" pair
  // (see buildCategoryTree.js); the chip only shows the child's short name.
  const deptLocked = (filters.departments && filters.departments.locked) || {}
  Object.keys((filters.departments && filters.departments.selected) || {}).forEach((name) => {
    if (filters.departments.selected[name]) {
      chips.push({
        group: 'Category',
        label: shortName(name) || name,
        locked: !!deptLocked[name],
        remove: () => removeAndRefetch((f) => { delete f.departments.selected[name] }),
      })
    }
  })

  // Charities
  const orgsLocked = (filters.orgs && filters.orgs.orgs_locked) || {}
  Object.keys((filters.orgs && filters.orgs.selected && filters.orgs.selected.orgs) || {}).forEach((name) => {
    if (filters.orgs.selected.orgs[name]) {
      chips.push({
        group: 'Charity',
        label: name,
        locked: !!orgsLocked[name],
        remove: () => removeAndRefetch((f) => { delete f.orgs.selected.orgs[name] }),
      })
    }
  })

  // Causes
  Object.keys((filters.orgs && filters.orgs.selected && filters.orgs.selected.causes) || {}).forEach((name) => {
    if (filters.orgs.selected.causes[name]) {
      chips.push({
        group: 'Cause',
        label: name,
        locked: false,
        remove: () => removeAndRefetch((f) => { delete f.orgs.selected.causes[name] }),
      })
    }
  })

  // Themes / Collections
  const themesLocked = (filters.themes && filters.themes.locked) || {}
  const themeLabel = !_.isEmpty(themesLocked) ? 'Theme' : 'Collection'
  Object.keys((filters.themes && filters.themes.selected) || {}).forEach((name) => {
    if (filters.themes.selected[name]) {
      chips.push({
        group: themeLabel,
        label: name,
        locked: !!themesLocked[name],
        remove: () => removeAndRefetch((f) => { delete f.themes.selected[name] }),
      })
    }
  })

  // Person / Location / Activity (details)
  const details = (filters.details && filters.details.selected) || {}
  ;[['people', 'Person'], ['place', 'Location'], ['activity', 'Activity']].forEach(([key, group]) => {
    Object.keys(details[key] || {}).forEach((name) => {
      if (details[key][name]) {
        chips.push({
          group,
          label: name,
          locked: false,
          remove: () => removeAndRefetch((f) => { delete f.details.selected[key][name] }),
        })
      }
    })
  })

  // Donation Type
  const donationTypeCode = filters.donation_type && filters.donation_type.code
  if (donationTypeCode) {
    chips.push({
      group: 'Donation Type',
      label: donationTypeCode,
      locked: false,
      remove: () => removeAndRefetch((f) => { f.donation_type.code = '' }),
    })
  }

  if (chips.length === 0) return null

  return (
    <div className="fb-meta-row">
      <div className="fb-meta-left">
        {chips.map((c, i) => (
          <Chip
            key={`${c.group}-${c.label}-${i}`}
            group={c.group}
            label={c.label}
            locked={c.locked}
            onRemove={c.remove}
          />
        ))}
        {showClearAll && (
          <button type="button" className="fb-clear" onClick={onClearAll}>
            Clear all
          </button>
        )}
      </div>
    </div>
  )
}
